import React, { useMemo } from "react";
import { noop } from "@common/helpers/function";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { BaseFilterProps } from "../../lib";
import styles from "./TaskAssigneeFilter.module.scss";
import {
    ASSIGNED_TO_CLIENT_OPTION_LABEL,
    ASSIGNED_TO_CLIENT_OPTION_VALUE,
    UNASSIGNED_OPTION_LABEL,
    UNASSIGNED_OPTION_VALUE,
} from "./taskAssigneeFilter.constants";

interface Props extends BaseFilterProps {
    /** List only organization members (org-level tasks page). */
    hasOrgMembersOnly?: boolean;
    /** Adds an "Assigned to client" option (org-level tasks page). */
    hasClientOption?: boolean;
}

export const TaskAssigneeFilter: React.FC<Props> = ({
    filters,
    onChange,
    hasOrgMembersOnly = false,
    hasClientOption = false,
}) => {
    const { data: workspaceMembers } = useCurrentWorkspaceMembers();
    const { data: orgMembers } = useCurrentOrganizationMembers({
        enabled: hasOrgMembersOnly,
    });
    const members = hasOrgMembersOnly ? orgMembers : workspaceMembers;

    const options = useMemo((): FilterMultiselectOption[] => {
        const memberOptions = (members ?? []).map((member) => ({
            value: member.user.id,
            label: member.user.name,
            searchableLabel: `${member.user.name} ${member.user.email ?? ""}`,
        }));
        return [
            { value: UNASSIGNED_OPTION_VALUE, label: UNASSIGNED_OPTION_LABEL },
            ...(hasClientOption
                ? [
                      {
                          value: ASSIGNED_TO_CLIENT_OPTION_VALUE,
                          label: ASSIGNED_TO_CLIENT_OPTION_LABEL,
                      },
                  ]
                : []),
            ...memberOptions,
        ];
    }, [members, hasClientOption]);

    const value = useMemo((): FilterMultiselectValue[] => {
        const specialValues: FilterMultiselectValue[] = [
            ...(filters.isTaskUnassigned ? [UNASSIGNED_OPTION_VALUE] : []),
            ...(hasClientOption && filters.isTaskAssignedToClient
                ? [ASSIGNED_TO_CLIENT_OPTION_VALUE]
                : []),
        ];
        return [...specialValues, ...(filters.taskAssigneeIds ?? [])];
    }, [
        filters.taskAssigneeIds,
        filters.isTaskUnassigned,
        filters.isTaskAssignedToClient,
        hasClientOption,
    ]);

    return (
        <div className={styles.filter}>
            <FilterMultiselect
                inDropdown={false}
                options={options}
                value={value}
                onChange={(val) => {
                    const ids = val.filter(
                        (v): v is number => typeof v === "number",
                    );
                    onChange({
                        taskAssigneeIds: ids.length > 0 ? ids : undefined,
                        isTaskUnassigned: val.includes(UNASSIGNED_OPTION_VALUE)
                            ? true
                            : undefined,
                        isTaskAssignedToClient: val.includes(
                            ASSIGNED_TO_CLIENT_OPTION_VALUE,
                        )
                            ? true
                            : undefined,
                    });
                }}
                name="taskAssigneeIds"
                onBlur={noop}
                searchable
            />
        </div>
    );
};
